import {
  createContext,
  Dispatch,
  Fragment,
  PropsWithChildren,
  SetStateAction,
  useState,
} from 'react';
import { IntlProvider } from 'react-intl';

import { translationMessages } from '../messages';
import {
  AvailableLocale,
  defaultRichTextElements,
  locale as defaultLocale,
  timezone as defaultTimezone,
} from './shared';

export type LanguageContextType = {
  locale: AvailableLocale;
  setLocale: Dispatch<SetStateAction<AvailableLocale>>;
  timezone: string;
  setTimezone: Dispatch<SetStateAction<string>>;
};

export const LanguageContext = createContext<LanguageContextType>(
  // Default context
  {
    locale: defaultLocale,
    setLocale: () => void 0,
    timezone: defaultTimezone,
    setTimezone: () => void 0,
  },
);

export default function LanguageProvider({ children }: PropsWithChildren) {
  const [locale, setLocale] = useState(defaultLocale);
  const [timezone, setTimezone] = useState(defaultTimezone);

  const handleError = (error: Error & { code: string }) => {
    if (
      import.meta.env.VITE_I18N_LOG_MISSING_TRANSLATIONS === 'true' &&
      error.code === 'MISSING_TRANSLATION'
    ) {
      // Make missing translations errors less visible as they are very verbose and can hide more valuable error messages
      // eslint-disable-next-line no-console
      console.warn(error.message);
    }
  };

  return (
    <LanguageContext.Provider
      value={{ locale, setLocale, timezone, setTimezone }}
    >
      <IntlProvider
        locale={locale}
        key={locale}
        messages={translationMessages[locale]}
        textComponent={Fragment}
        defaultRichTextElements={defaultRichTextElements}
        onError={handleError}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
}
